module Client.App

open Fable.Core
open Fable.Core.JsInterop

open Fable.Core
open Fable.Import
open Elmish
open Elmish.React
open Elmish.Navigation
open Client.Pages
open Elmish.UrlParser
open Elmish.HMR
open Elmish.Navigation
open Shared.Domain

JsInterop.importSideEffects "whatwg-fetch"
JsInterop.importSideEffects "@babel/polyfill"

// Model
type PageModel =
  | HomeModel of Home.Model
  | TenantModel of Tenant.Model
  | AdminModel of Admin.Model
  | PasswordResetModel of PasswordReset.Model
  | AlcoholAuditModel of AlcoholAudit.Model
  | ReferralModel of Referral.Model
  | ConfirmReferralModel of ConfirmReferral.Model
  | TrainingModel of Training.Model


type Model =
    { 
        Menu : Menu.Model
        PageModel : PageModel
    }

/// Application level messages
type Msg = 
  | HomeMsg of Home.Msg
  | MenuMsg of Menu.Msg
  | TenantMsg of Tenant.Msg
  | AdminMsg of Admin.Msg
  | PasswordResetMsg of PasswordReset.Msg
  | AlcoholAuditMsg of AlcoholAudit.Msg
  | ReferralMsg of Referral.Msg
  | ConfirmReferralMsg of ConfirmReferral.Msg
  | TrainingMsg of Training.Msg
 
let checkMenu tenant (menu: Menu.Model) title : Menu.Model * Cmd<Menu.Msg> =
    match menu.Menu with
    | Some _ -> 
        { menu with SectionTitle = title; OpenMenu = None },[]
    | None ->
        let m,cmd = Menu.init (Some tenant) true
        { m with SectionTitle = title; OpenMenu = None }, cmd


/// Parse the url and update internal state
let urlUpdate (result: Client.Pages.Page option) model =    
    match result with
    | None ->
        Browser.Dom.console.error("Error parsing url: " + Browser.Dom.window.location.href)
        let m,cmd = Home.init()
        { model with PageModel = HomeModel m; Menu = { model.Menu with SectionTitle = ""; Menu = None }}, Cmd.map HomeMsg (Cmd.batch [cmd; Navigation.modifyUrl (toHash Page.Home)])
    | Some Page.Home -> 
        let m,cmd = Home.init()
        { model with PageModel = HomeModel m; Menu = { model.Menu with SectionTitle = ""; Menu = None; Tenant = None }}, Cmd.map HomeMsg cmd
    | Some (Page.InfoPage (tenant, slug)) ->
        let m,cmd = Tenant.init tenant slug
        let mm,mcmd = checkMenu tenant model.Menu ""
        { model with PageModel = TenantModel m; Menu = mm}, Cmd.batch [Cmd.map MenuMsg mcmd; Cmd.map TenantMsg cmd]
    | Some (Page.Tenant tenant) ->
        let m,cmd = Tenant.init tenant ""
        let mm,mcmd = checkMenu tenant model.Menu ""
        { model with PageModel = TenantModel m; Menu = mm}, Cmd.batch [Cmd.map MenuMsg mcmd; Cmd.map TenantMsg cmd]
    | Some (Page.AlcoholAudit tenant) ->
        let m,cmd = AlcoholAudit.init tenant
        let mm,mcmd = checkMenu tenant model.Menu "Alcohol Audit"
        { model with PageModel = AlcoholAuditModel m; Menu = mm}, Cmd.batch [Cmd.map MenuMsg mcmd; Cmd.map AlcoholAuditMsg cmd]
    | Some (Page.SelfReferral tenant) ->
        // if we already have an alcohol audit then leave it in place
        let m,cmd = 
            match model.PageModel with
            | ReferralModel m -> Referral.init tenant (m.Audit)
            | _ -> Referral.init tenant None
        let mm,mcmd = checkMenu tenant model.Menu "Referral"
        { model with PageModel = ReferralModel m; Menu = mm}, Cmd.batch [Cmd.map MenuMsg mcmd; Cmd.map ReferralMsg cmd]
    | Some (Page.ConfirmReferral (tenant, guid)) ->
        let m,cmd = ConfirmReferral.init tenant guid
        let mm,mcmd = checkMenu tenant model.Menu "Confirm Referral"
        { model with PageModel = ConfirmReferralModel m; Menu = mm}, Cmd.batch [Cmd.map MenuMsg mcmd; Cmd.map ConfirmReferralMsg cmd]
    | Some (Page.PasswordReset guid) ->
        let m,cmd = PasswordReset.init guid
        { model with PageModel = PasswordResetModel m; }, Cmd.map PasswordResetMsg cmd
    | Some (Page.Admin (tenant, action)) ->
        match BrowserLocalStorage.load<JWT> "user" with
        | Result.Ok user when TenantRef.get user.Account.Tenant = tenant ->
            let m,cmd = Admin.init user action
            { model with PageModel = AdminModel m; Menu = { model.Menu with SectionTitle = "Admin" }}, Cmd.map AdminMsg cmd
        | _ -> 
            let m,cmd = Home.init()
            let batch = Cmd.batch [ Cmd.map HomeMsg cmd; Navigation.modifyUrl (toHash Page.Home) ]
            { model with PageModel = HomeModel m; Menu = { model.Menu with SectionTitle = ""; Menu = None }}, batch   
    | Some (Page.Training (tenant, token, slug)) ->
        let m,cmd = Training.init tenant token slug
        { model with PageModel = TrainingModel m }, Cmd.map TrainingMsg cmd
         
/// Sets up the default state for the app
let init result =
    // sub-init from the menu
    
    let tenant, loadMenu = 
        match result with
        | Some (Page.InfoPage (tenant, _)) 
        | Some (Page.AlcoholAudit tenant)
        | Some (Page.SelfReferral tenant)
        | Some (Page.ConfirmReferral (tenant,_))
        | Some (Page.Tenant tenant) -> Some tenant, true
        | Some (Page.Admin (tenant,_)) -> Some tenant, false
        | _ ->
            None, false

    // Ignore command so it's never double fired by urlUpdate
    let homeModel, _ = Home.init()
    let menu,menuCmd = Menu.init tenant loadMenu
    let m =
        { 
            Menu = menu
            PageModel = HomeModel homeModel
        }

    // parse the url and re-configure state
    let m,cmd = urlUpdate result m
    let menuCmd = Cmd.map MenuMsg menuCmd
    m, Cmd.batch[cmd; menuCmd]

let menuActions (msg: string option) model = 
    {
        model.Menu with 
            SuccessMessage = (if msg.IsSome then msg.Value else "");
            OpenMenu = None
            MenuCollapse = true
    }

/// Updates the state of the application given a message and model
let update msg model =
    match msg, model.PageModel with
    | TenantMsg m, TenantModel h ->
        let m,cmd, success = Tenant.update m h
        let cmd = Cmd.map TenantMsg cmd
        let menuUpdate = Menu.shouldSwitchTenant model.Menu m.Tenant.Value
        { model with PageModel = TenantModel m; Menu = (menuActions success model) }, Cmd.batch [cmd; Cmd.map MenuMsg menuUpdate]
    | MenuMsg m, _ -> 
        let m,cmd  = Menu.update m (model.Menu)
        let cmd = Cmd.map MenuMsg cmd
        { model with Menu = m },cmd
    | AdminMsg m, AdminModel am ->
        let m,cmd = Admin.update m am
        let cmd = Cmd.map AdminMsg cmd
        { model with PageModel = AdminModel m }, cmd
    | AlcoholAuditMsg (AlcoholAudit.ContinueToReferral (audit, tenant)), _ ->
        let m,cmd = Referral.init tenant (Some audit)
        { model with PageModel = ReferralModel m; Menu = { model.Menu with SectionTitle = "Referral" }}, Cmd.map ReferralMsg cmd
    | AlcoholAuditMsg m, AlcoholAuditModel am ->
        let m,cmd,success = AlcoholAudit.update m am
        let cmd = Cmd.map AlcoholAuditMsg cmd
        { model with PageModel = AlcoholAuditModel m; Menu = (menuActions success model) }, cmd
    | ReferralMsg m, ReferralModel am ->
        let m,cmd,success = Referral.update m am
        let cmd = Cmd.map ReferralMsg cmd
        { model with PageModel = ReferralModel m; Menu = (menuActions success model) }, cmd
    | PasswordResetMsg msg, PasswordResetModel m ->
        let m,cmd,success = PasswordReset.update msg m
        let cmd = Cmd.map PasswordResetMsg cmd
        { model with PageModel = PasswordResetModel m; Menu = (menuActions success model) }, cmd
    | ConfirmReferralMsg msg, ConfirmReferralModel m ->
        let m,cmd,success = ConfirmReferral.update msg m
        let cmd = Cmd.map ConfirmReferralMsg cmd
        { model with PageModel = ConfirmReferralModel m; Menu = (menuActions success model) }, cmd
    | HomeMsg m, HomeModel am ->
        let m,cmd = Home.update m am
        let cmd = Cmd.map HomeMsg cmd
        { model with PageModel = HomeModel m; }, cmd
    | TrainingMsg msg, TrainingModel m ->
        let m',cmd' = Training.update msg m
        { model with PageModel = TrainingModel m' }, Cmd.map TrainingMsg cmd'

// VIEW

open Fable.React
open Fable.React.Props
open Client.Style

/// Constructs the view for a page given the model and dispatcher.
let viewPage model dispatch =
    match model.PageModel with 
    | HomeModel m -> Menu.view model.Menu (MenuMsg >> dispatch) (Home.view m (HomeMsg >> dispatch))
    | TenantModel m -> Menu.view model.Menu (MenuMsg >> dispatch) (Tenant.view m (TenantMsg >> dispatch))
    | AdminModel m -> Admin.view m (AdminMsg >> dispatch)
    | AlcoholAuditModel m -> Menu.view model.Menu (MenuMsg >> dispatch) (AlcoholAudit.view m (AlcoholAuditMsg >> dispatch))
    | ReferralModel m -> Menu.view model.Menu (MenuMsg >> dispatch) (Referral.view m (ReferralMsg >> dispatch))
    | ConfirmReferralModel m -> Menu.view model.Menu (MenuMsg >> dispatch) (ConfirmReferral.view m (ConfirmReferralMsg >> dispatch))
    | PasswordResetModel m -> PasswordReset.view m (PasswordResetMsg >> dispatch)   
    | TrainingModel m -> Training.view m (TrainingMsg >> dispatch)   

open Elmish.React

let view model dispatch =
  div []
    [ 
        div [
            ClassName "expanding-parent"
            Style [ MinHeight "100vh" ]
        ] (viewPage model dispatch)
    ]

let handleExn (msg,exn) =
    Browser.Dom.console.error exn
    Browser.Dom.console.error msg


// App
Program.mkProgram init update view
|> Program.toNavigable (parseHash pageParser) urlUpdate
#if DEBUG
|> Program.withConsoleTrace
#endif
|> Program.withReactBatched "elmish-app"
#if DEBUG
|> Program.withErrorHandler handleExn
#endif
|> Program.run
